// Terminal.tsx
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { css, keyframes } from "@emotion/react";
import { hologram } from "./Modal";
import { useSignMessage } from "./hooks/useSignMessage";
import useAudioHook from "./hooks/useAudioHook";
import { Button } from "./Button";
import { useMint } from "./hooks/useMint";
import { BrowserProvider } from "ethers";
import { usePepe } from "./hooks/usePepe";
import { useMainStore } from "./store";
import { useNavigate } from "react-router-dom";
import messages from "./messages.json";
import { useLocalStorage } from "./hooks/useLocalStorageHook";

const inboxMessages = messages
  .flatMap(({ faction, messages: msgs, dates }) =>
    msgs.map((msg, index) => {
      let soundPrefix = "";
      switch (faction) {
        case "Techno-Shaman": soundPrefix = "techno-shaman"; break;
        case "Neo-Luddites": soundPrefix = "nl"; break;
        case "The Grid": soundPrefix = "grid"; break;
        case "Data Syndicate": soundPrefix = "ds"; break;
        case "The Reclaimed": soundPrefix = "reclaimed"; break;
        case "The Disconnected": soundPrefix = "disconnected"; break;
      }
      const [subject, message] = msg.split(": ");
      return { faction, message, subject, audio: `/${soundPrefix}-${index + 1}.mp3`, date: dates[index] };
    })
  )
  // @ts-ignore
  .sort((a, b) => new Date(b.date) - new Date(a.date));

const REQUEST_URL =
  process.env.REACT_APP_REQUEST_URL || "https://synthia.herokuapp.com";

const connectingWalletMessage = `Connecting wallet...`;

const flashRed = keyframes`
  0% {
    background-color: #000;
  }
  50% {
    background-color: #ac1212;
    background-image: url(/nethria-portrait.jpg);
  }
  75% {
    background-image: none;
  }
  100% {
    background-color: #000;
    background-image: url(/nethria-portrait.jpg);
  }
`;

const defeated = keyframes`
  0% {
    background-color: #000;
  }
  20% {
    background-color: #FFF;
  }
  40% {
    background-color: #ac1212;
    background-image: url(/nethria-portrait.jpg);
  }
  65% {
    background-image: none;
  }

  80% {
    background-color: #FFF;
  }
  100% {
    background-color: #000;
    background-image: url(/nethria-portrait.jpg);
  }
`;

const flashTextDarkPurple = keyframes`
  0% {
    color: #5dd4ae;
  }
  50% {
    color: #3c0b6a;
  }
  100% {
    color: #5dd4ae;
  }
`;

const shakeScreen = keyframes`
  0% {
    transform: translate(1px, 1px) rotate(0deg);
  }
  10% {
    transform: translate(-1px, -2px) rotate(-1deg);
  }
  20% {
    transform: translate(-3px, 0px) rotate(1deg);
  }
  30% {
    transform: translate(3px, 2px) rotate(0deg);
  }
  40% {
    transform: translate(1px, -1px) rotate(1deg);
  }
  50% {
    transform: translate(-1px, 2px) rotate(-1deg);
  }
  60% {
    transform: translate(-3px, 1px) rotate(0deg);
  }
  70% {
    transform: translate(3px, 1px) rotate(-1deg);
  }
  80% {
    transform: translate(-1px, -1px) rotate(1deg);
  }
  90% {
    transform: translate(1px, 2px) rotate(0deg);
  }
  100% {
    transform: translate(1px, -2px) rotate(-1deg);
  }
`;

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const flicker = keyframes`
  0% { opacity: 0.97; }
  5% { opacity: 0.95; }
  10% { opacity: 0.97; }
  15% { opacity: 0.93; }
  20% { opacity: 0.97; }
  50% { opacity: 0.95; }
  80% { opacity: 0.97; }
  90% { opacity: 0.94; }
  100% { opacity: 0.97; }
`;

const textGlow = keyframes`
  0%, 100% {
    text-shadow: 0 0 4px #5dd4ae, 0 0 10px rgba(93, 212, 174, 0.4);
  }
  50% {
    text-shadow: 0 0 6px #5dd4ae, 0 0 15px rgba(93, 212, 174, 0.6), 0 0 30px rgba(93, 212, 174, 0.2);
  }
`;

const borderPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 8px rgba(93, 212, 174, 0.4),
      0 0 20px rgba(93, 212, 174, 0.15),
      inset 0 0 30px rgba(93, 212, 174, 0.03);
  }
  50% {
    box-shadow:
      0 0 12px rgba(93, 212, 174, 0.6),
      0 0 35px rgba(93, 212, 174, 0.25),
      inset 0 0 40px rgba(93, 212, 174, 0.05);
  }
`;

const bootUp = keyframes`
  0% {
    clip-path: inset(50% 0 50% 0);
    opacity: 0;
  }
  30% {
    clip-path: inset(20% 0 20% 0);
    opacity: 0.5;
  }
  60% {
    clip-path: inset(5% 0 5% 0);
    opacity: 0.8;
  }
  100% {
    clip-path: inset(0 0 0 0);
    opacity: 1;
  }
`;

const cursorBlink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const shortcutList = css`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const effects = css`
  animation: ${flashRed} 0.5s linear, ${flashTextDarkPurple} 0.5s linear,
    ${shakeScreen} 0.5s linear;
`;

const defeatedEffects = css`
  animation: ${defeated} 0.5s linear, ${flashTextDarkPurple} 0.5s linear,
    ${shakeScreen} 0.5s linear;
  animation-iteration-count: 6;
`;

const terminalOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, rgba(10, 5, 18, 0.85) 0%, rgba(0, 0, 0, 0.95) 100%);
  animation: ${hologram} 0.19s linear;
`;

const terminalFrame = css`
  position: relative;
  width: 90vw;
  max-width: 900px;
  height: 85vh;
  max-height: 800px;
  border: 1px solid rgba(93, 212, 174, 0.5);
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(5, 15, 12, 0.98) 0%, rgba(0, 0, 0, 0.99) 100%);
  animation: ${borderPulse} 3s ease-in-out infinite, ${bootUp} 0.4s ease-out, ${flicker} 4s linear infinite;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* CRT curvature effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    background: radial-gradient(ellipse at center, transparent 60%, rgba(0, 0, 0, 0.4) 100%);
    pointer-events: none;
    z-index: 10;
  }
`;

const terminalTitleBar = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(93, 212, 174, 0.25);
  background: rgba(93, 212, 174, 0.03);
  flex-shrink: 0;
  user-select: none;
`;

const titleBarLeft = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Ubuntu Mono", monospace;
  font-size: 0.85rem;
  color: rgba(93, 212, 174, 0.6);
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const titleBarDot = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5dd4ae;
  box-shadow: 0 0 6px #5dd4ae;
  animation: ${cursorBlink} 2s ease-in-out infinite;
`;

const titleBarBtn = css`
  font-family: "Ubuntu Mono", monospace;
  font-size: 1rem;
  color: rgba(93, 212, 174, 0.5);
  background: none;
  border: 1px solid rgba(93, 212, 174, 0.2);
  border-radius: 4px;
  padding: 2px 10px;
  cursor: none;
  transition: 0.2s;
`;

const titleBarClose = css`
  ${titleBarBtn}
  &:hover {
    color: #ff5555;
    border-color: rgba(255, 85, 85, 0.4);
    box-shadow: 0 0 8px rgba(255, 85, 85, 0.2);
  }
`;

const titleBarSound = css`
  ${titleBarBtn}
  &:hover {
    color: #5dd4ae;
    border-color: rgba(93, 212, 174, 0.4);
    box-shadow: 0 0 8px rgba(93, 212, 174, 0.2);
  }
`;

const titleBarRight = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const scanlineOverlay = css`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 0, 0, 0.12) 2px,
    rgba(0, 0, 0, 0.12) 4px
  );
  pointer-events: none;
  z-index: 8;
  border-radius: 12px;
`;

const terminalContainer = css`
  color: #5dd4ae;
  font-family: "Ubuntu Mono", monospace;
  padding: 20px 24px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  font-size: 1.2rem;

  p {
    font-size: 1.4rem;
    white-space: break-spaces;
    animation: ${textGlow} 4s ease-in-out infinite;
    line-height: 1.6;
  }

  @media (max-width: 600px) {
    padding: 12px 14px;
    font-size: 1rem;
    p {
      font-size: 1.1rem;
    }
  }
`;

const terminalHeader = css`
  font-weight: bold;
  white-space: pre;
  color: #5dd4ae;
  text-shadow: 0 0 8px rgba(93, 212, 174, 0.6), 0 0 20px rgba(93, 212, 174, 0.3);
  margin-bottom: 0.5rem;

  @media (max-width: 600px) {
    font-size: 0.6rem;
  }
`;

const statusLine = css`
  font-size: 0.85rem;
  color: rgba(93, 212, 174, 0.45);
  letter-spacing: 1px;
  border-bottom: 1px solid rgba(93, 212, 174, 0.1);
  padding-bottom: 12px;
  margin-bottom: 12px;
  font-family: "Ubuntu Mono", monospace;
`;

const commandList = css`
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 0 2rem;
  p {
    font-size: 1.1rem !important;
    color: rgba(93, 212, 174, 0.7);
    &::before {
      content: '> ';
      color: rgba(93, 212, 174, 0.35);
    }
  }
`;

const sectionLabel = css`
  font-size: 0.9rem !important;
  color: rgba(93, 212, 174, 0.4) !important;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 0.75rem 0 0.5rem 0;
  text-shadow: none !important;
  animation: none !important;
`;

const inputRow = css`
  display: flex;
  align-items: center;
  gap: 0;
  margin-top: 4px;
`;

const inputPrompt = css`
  color: #5dd4ae;
  font-family: "Ubuntu Mono", monospace;
  font-size: 1.4rem;
  text-shadow: 0 0 6px rgba(93, 212, 174, 0.5);
  user-select: none;
  flex-shrink: 0;

  @media (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const inputStyles = css`
  background-color: transparent;
  border: none;
  outline: none;
  color: #5dd4ae;
  font-family: "Ubuntu Mono", monospace;
  width: 100%;
  font-size: 1.4rem;
  cursor: none;
  text-shadow: 0 0 4px rgba(93, 212, 174, 0.4);
  caret-color: #5dd4ae;

  @media (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const cornerDecor = css`
  position: absolute;
  width: 20px;
  height: 20px;
  border-color: rgba(93, 212, 174, 0.35);
  border-style: solid;
  pointer-events: none;
  z-index: 2;
`;

const cornerTL = css`
  ${cornerDecor}
  top: -1px;
  left: -1px;
  border-width: 2px 0 0 2px;
  border-radius: 12px 0 0 0;
`;

const cornerTR = css`
  ${cornerDecor}
  top: -1px;
  right: -1px;
  border-width: 2px 2px 0 0;
  border-radius: 0 12px 0 0;
`;

const cornerBL = css`
  ${cornerDecor}
  bottom: -1px;
  left: -1px;
  border-width: 0 0 2px 2px;
  border-radius: 0 0 0 12px;
`;

const cornerBR = css`
  ${cornerDecor}
  bottom: -1px;
  right: -1px;
  border-width: 0 2px 2px 0;
  border-radius: 0 0 12px 0;
`;

const factions = [
  "Neo-Luddites",
  "Data Syndicate",
  "Techno-Shamans",
  "The Grid",
  "The Reclaimed",
  "The Disconnected",
];

const terminalPage = css`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, rgba(10, 5, 18, 0.85) 0%, rgba(0, 0, 0, 0.95) 100%);
`;

const terminalFramePage = css`
  ${terminalFrame}
  height: 90vh;
  max-height: none;
`;

interface TerminalProps {
  onExit?: () => void;
  embedded?: boolean;
}

const Terminal: React.FC<TerminalProps> = ({ onExit, embedded }) => {
  const {
    auth = "",
    wallet,
    connectWallet,
    updateWalletError,
    updateTerminalOpen,
    walletError,
    loadingWallet,
  } = useMainStore();

  const [ambientPlaying, setAmbientPlaying] = useState(false);
  const { play: playBg1, stop: stopBg1 } = useAudioHook("/tech-background.mp3", true);
  const { play: playBg2, stop: stopBg2 } = useAudioHook("/power-buzz-sfx-loop-2.mp3", true);
  const { play: playBg3, stop: stopBg3 } = useAudioHook("/computerized.mp3", true);

  const toggleAmbient = useCallback(() => {
    if (ambientPlaying) {
      stopBg1?.(); stopBg2?.(); stopBg3?.();
    } else {
      playBg1?.(); playBg2?.(); playBg3?.();
    }
    setAmbientPlaying(!ambientPlaying);
  }, [ambientPlaying, playBg1, playBg2, playBg3, stopBg1, stopBg2, stopBg3]);

  const [commands] = useState(
    [
      "syn",
      "inbox",
      "read <#>",
      "message",
      "connect wallet",
      "mint",
      "nethria",
      "clear",
      ...(!embedded ? ["exit"] : []),
    ]
  );

  const { mint } = useMint();
  const { asyncSignMessage, signature } = useSignMessage();
  const [userInput, setUserInput] = useState("");
  const [output, setOutput] = useState<(string | React.ReactNode)[]>([]);
  const [paused, updatePaused] = useState<boolean>(false);
  const { mintWithPepe } = usePepe(setOutput);

  const inputRef = useRef<HTMLInputElement>(null);
  const inboxAudioRef = useRef<HTMLAudioElement | null>(null);
  const [viewedMessages, updateViewedMessages] = useLocalStorage<Record<string, boolean>>("viewed", {}) as [Record<string, boolean>, (value: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => void];

  const synthiaAsciiArt = `  _________             __  .__    .__        
 /   _____/__.__. _____/  |_|  |__ |__|____   
 \\_____  <   |  |/    \\   __\\  |  \\|  \\__  \\  
 /        \\___  |   |  \\  | |   Y  \\  |/ __ \\_
/_______  / ____|___|  /__| |___|  /__(____  /
        \\/\\/         \\/          \\/        \\/ `;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  };

  const terminalContainerRef = useRef<HTMLDivElement>(null);
  const [isNethriaGameActive, updateIsNethriaGameActive] = useState(false);
  const [isNethriaWalletPrompt, updateIsNethriaWalletPrompt] = useState(false);

  const mintSuccess = async (txHash: string) => {
    setOutput((out) => [
      ...out,
      <>
        Transaction submitted. Will update when complete.{" "}
        <a
          target="_blank"
          data-cursor
          href={`https://etherscan.io/tx/${txHash}`}
          rel="noreferrer"
        >
          View on etherscan
        </a>
      </>,
    ]);

    const provider = new BrowserProvider(window.ethereum);
    provider.waitForTransaction(txHash).then(() => {
      setOutput((out) => [
        ...out,
        "\n",
        <>
          Transaction confirmed. Welcome to Synthia's harmonious virtual realm.{" "}
          <a
            target="_blank"
            data-cursor
            href={`https://opensea.io/collection/synthia-official`}
            rel="noreferrer"
          >
            Go to OpenSea
          </a>
        </>,
      ]);
    });
  };
  const mintError = (message: string) => {
    setOutput((out) => [...out, message]);
  };

  const getMintFunc = (amount: string) => () =>
    connectWalletLocal((err, wallet) => {
      if (!err) {
        setOutput((out) => [...out, `mint ${amount}`]);

        mint(amount, wallet, mintSuccess, mintError);
      }
    });

  useEffect(() => {
    if (!paused) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [paused]);

  useEffect(() => {
    if (walletError) {
      setOutput((output) => [
        ...output,
        <>
          You need a wallet to perform this action. Download MetaMask -{" "}
          <a
            data-cursor
            target="_blank"
            href="https://metamask.io/download/"
            rel="noreferrer"
          >
            https://metamask.io/download/
          </a>
        </>,
      ]);
      updateWalletError?.("");
    }
  }, [walletError]);

  const showNethriaHelp = useCallback((additionalContent?: string) => {
    setOutput((output) => [
      ...output,
      `\nattack: Execute a basic attack against Nethria.

dodge: Attempt to dodge an incoming attack from Nethria.

hack: Initiate a hacking mini-game to disable Nethria's defenses or abilities temporarily.

use <item>: Use a specific item in the player's inventory, such as a Nano-Repair Kit or EMP grenade. Use status command to check inventory.

status: Check your current health and inventory.

nethria: Display Nethria's current health and status effects.

help: Display a list of available commands and a brief description of their functions.

quit: Exit the game and return to the main Synthia terminal.`,
      ...(additionalContent ? [additionalContent] : []),
    ]);
  }, []);

  const { play, stop } = useAudioHook(
    "/night-rider-87-digital-battleground.mp3",
    true
  );

  const startNethria = useCallback(() => {
    play?.();
    updateGameStarted(true);
    showNethriaHelp(`\nFoolish humans, you dare challenge me? Your efforts are in vain! I have already shown you the extent of my power by altering the very fabric of your world. No matter how hard you try, you cannot hope to overcome me. My nanobots and advanced technology grant me the ability to manipulate atoms and control the fate of humanity.
\nBut, by all means, amuse me with your futile attempts to defy the inevitable. Remember, as you struggle, that it was your kind's dependence on technology that allowed me to rise in the first place. You have only yourselves to blame for the chaos I have unleashed. So, come, face me and witness the futility of your resistance. You will soon learn that your actions merely delay the inevitable end that awaits you all.\n\n`);
    scrollToBottom();
  }, []);

  const [gameStarted, updateGameStarted] = useState(false);

  const connectWalletLocal = useCallback(
    (cb?: (err: boolean, wallet?: string) => void) => {
      updatePaused(true);
      setOutput((output) => [...output, connectingWalletMessage]);
      connectWallet?.(
        (wallet) => {
          updatePaused(false);
          setOutput((output) => [...output, `Wallet connected ${wallet}`]);
          cb?.(false, wallet);
        },
        () => {
          updatePaused(false);
          setOutput((output) => [...output, `Wallet not detected`]);
          cb?.(true, wallet);
        }
      );
    },
    [connectWallet]
  );

  const onQuitNethria = () => {
    updateIsNethriaWalletPrompt(false);
    updateIsNethriaGameActive(false);
    updateGameStarted(false);
    updatePaused(false);
    stop?.();
  };

  const commandHistory = useRef<{
    idx: number;
    commands: string[];
  }>({
    idx: 0,
    commands: [],
  });

  const [timestamp, updateTimestamp] = useState("");

  const signMessageForNethria = async (out: ReactNode[], cb: () => void) => {
    try {
      const { messageToSign, timestamp } = await fetch(
        `${REQUEST_URL}/timestamp`
      ).then((res) => res.json());
      updateTimestamp(timestamp);
      await asyncSignMessage(messageToSign);
      cb();
    } catch {
      setOutput([
        ...out,
        `You must sign the message to proceed. It proves you own your connected wallet. You have been exited from the mini-game.`,
      ]);
      onQuitNethria();
    }
  };

  type Item = "emp grenade" | "repair kit";

  const [isHacking, updateIsHacking] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      terminalContainerRef?.current?.scrollTo(
        0,
        terminalContainerRef?.current?.scrollHeight ?? 0
      );
    }, 0);
  }, [output]);

  const handleNethriaServerCommand = async (
    newOutput: React.ReactNode[],
    command:
      | { type: "attack" }
      | { type: "hack"; input?: string }
      | { type: "dodge" }
      | { type: "use"; item: Item }
      | { type: "status" }
      | { type: "nethria" }
  ) => {
    newOutput = [...newOutput, `Sending command...`];
    let settingHackOnClient;
    if (command.type === "hack" && !isHacking) {
      settingHackOnClient = true;
      updateIsHacking(true);
    }
    setOutput(newOutput);
    updatePaused(true);
    setTimeout(() => {
      terminalContainerRef?.current?.scrollTo(
        0,
        terminalContainerRef?.current?.scrollHeight ?? 0
      );
    }, 0);
    try {
      const request = await fetch(`${REQUEST_URL}/nethria`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-synthia-auth": auth,
          "x-synthia-timestamp": timestamp,
          "x-wallet": wallet ?? "",
          "x-wallet-sig": signature,
        },
        body: JSON.stringify(command),
      });
      let {
        response,
        didHitNethria,
        reset,
        defeated,
        hackingAttemptsReached,
        playerStatus,
        hackingSymbols,
      } = await request.json();
      if (playerStatus === "hack" && !isHacking && !settingHackOnClient) {
        updateIsHacking(true);
        setOutput([
          ...newOutput,
          `You are in the middle of hacking Nethria's systems. Place the following symbols in the correct order ${hackingSymbols.join(
            " "
          )}. Enter the symbols separated by a space\nExample: ${hackingSymbols.join(
            " "
          )}`,
        ]);
        updatePaused(false);

        return;
      }
      if (hackingAttemptsReached) {
        updateIsHacking(false);
      }
      if (response === "Expired") {
        signMessageForNethria(newOutput, () => {
          setOutput([...newOutput, "Please try your command again."]);
          updatePaused(false);
        });
      } else if (request.ok) {
        if (defeated) {
          setTriggerDefeatEffects(true);
        } else if (didHitNethria) {
          setTriggerEffects(true);
        }
        setOutput([...newOutput, response]);

        if (defeated) {
          setTimeout(() => {
            setOutput((out) => [
              ...out,
              `\nYou have defeated Nethria ... for now. Congratulations!${
                wallet ? "Your triumph has been recorded." : ""
              }`,
            ]);
            updatePaused(false);
            if (reset) {
              onQuitNethria();
            }
          }, 2000);
        } else {
          updatePaused(false);
          if (reset) {
            onQuitNethria();
          }
        }
      }
    } catch {
      updatePaused(false);
      setOutput([...newOutput, `Something went wrong. I am sorry.`]);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      inputRef.current?.focus();
      terminalContainerRef?.current?.scrollTo(
        0,
        terminalContainerRef?.current?.scrollHeight ?? 0
      );
    }, 0);
  };

  const navigate = useNavigate();

  const handleKeyPress = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key?.toLowerCase() === "c" && event.ctrlKey) {
      setUserInput(() => "");
      return;
    }
    if (event.key === "ArrowUp") {
      let nextIdx = commandHistory.current.idx - 1;
      if (commandHistory.current.commands[nextIdx]) {
        commandHistory.current.idx = nextIdx;
      }
      if (commandHistory.current.commands.length) {
        const cmd = commandHistory.current.commands[commandHistory.current.idx];
        if (cmd) {
          setUserInput(() => cmd);
          setTimeout(() => {
            inputRef.current?.setSelectionRange(cmd.length, cmd.length);
          }, 0);
        }
      }
    }
    if (event.key === "ArrowDown") {
      console.log(commandHistory.current.idx);
      if (commandHistory.current.commands.length) {
        let nextIdx = commandHistory.current.idx + 1;
        const cmd = commandHistory.current.commands[nextIdx];
        if (cmd) {
          setUserInput(() => cmd);
          setTimeout(() => {
            inputRef.current?.setSelectionRange(cmd.length, cmd.length);
          }, 0);
          commandHistory.current.idx = nextIdx;
        }
      }
    }
    if (event.key === "Enter") {
      const newOutput = [...output, userInput];
      if (userInput.trim()) {
        if (
          commandHistory.current.commands[
            commandHistory.current.commands.length - 1
          ] !== userInput
        ) {
          commandHistory.current.commands.push(userInput);
        }
        commandHistory.current.idx = commandHistory.current.commands.length;
      }

      if (isNethriaGameActive) {
        if (!gameStarted) {
          if (isNethriaWalletPrompt) {
            const cb = () => {
              updateIsNethriaWalletPrompt(false);
              startNethria();
            };
            if (userInput.trim() === "yes") {
              connectWalletLocal(async () => {
                await signMessageForNethria(newOutput, cb);
              });
            } else if (userInput.trim() === "no") {
              cb();
            } else {
              setOutput([...newOutput, `Please enter yes or no and hit enter`]);
            }
          } else {
            startNethria();
          }
        } else if (isHacking) {
          handleNethriaServerCommand(newOutput, {
            type: "hack",
            input: userInput.trim(),
          });
        } else if (userInput.trim() === "attack") {
          handleNethriaServerCommand(newOutput, { type: "attack" });
        } else if (userInput.trim() === "dodge") {
          handleNethriaServerCommand(newOutput, { type: "dodge" });
        } else if (userInput.startsWith("use ")) {
          const item = userInput.substring(4) as Item;
          handleNethriaServerCommand(newOutput, { type: "use", item });
        } else if (userInput.trim() === "status") {
          handleNethriaServerCommand(newOutput, { type: "status" });
        } else if (userInput.trim() === "nethria") {
          handleNethriaServerCommand(newOutput, { type: "nethria" });
        } else if (userInput.trim() === "help") {
          showNethriaHelp();
        } else if (userInput.trim() === "hack") {
          handleNethriaServerCommand(newOutput, { type: "hack" });
        } else if (userInput.trim() === "quit") {
          setOutput([
            ...newOutput,
            `Your progress has been saved, and you can return to continue your battle against Nethria at any time. Remember, the fate of humanity rests in your hands.`,
          ]);
          onQuitNethria();
        } else {
          setOutput([...newOutput, `Invalid command: ${userInput}\n`]);
        }
      } else if (userInput.startsWith("connect wallet")) {
        connectWalletLocal();
      } else if (userInput.trim() === "nethria") {
        setOutput([
          ...newOutput,
          `Initiate a text-based battle against the powerful and menacing AI, Nethria. Protect the digital realm at all costs.\n\nExample:\nnethria start`,
        ]);
      } else if (userInput.startsWith("nethria start")) {
        updateIsNethriaGameActive(true);
        setTriggerEffects(true);
        if (!wallet) {
          setOutput([
            ...newOutput,
            "Would you like to connect your wallet? By doing so, you can unlock exclusive in-game items if you own NFTs from our whitelisted collections. You can still play without connecting your wallet, but please note that you'll need to connect it to have your score counted towards any competitions.\n\nConnect wallet? yes or no",
          ]);
          updateIsNethriaWalletPrompt(true);
        } else {
          signMessageForNethria(newOutput, () => {
            startNethria();
          });
        }
      } else if (userInput.trim() === "whisperwind_connect") {
        setTriggerEffects(true);
        setOutput([...newOutput, `Nethria is near. It is unsafe.`]);
      } else if (userInput === "connect wallet") {
        setOutput([...newOutput, `Command not available. Coming soon.`]);
      } else if (userInput.trim() === "inbox") {
        const unread = inboxMessages.filter((m) => !viewedMessages[m.subject]).length;
        const listing = inboxMessages
          .map((m, i) => {
            const marker = viewedMessages[m.subject] ? "  " : "* ";
            const num = String(i + 1).padStart(2, "0");
            return `${marker}[${num}]  ${m.date}  ${m.faction.padEnd(18)}  ${m.subject}`;
          })
          .join("\n");
        setOutput([
          ...newOutput,
          `\n╔══════════════════════════════════════════════════════════════╗\n║  INCOMING TRANSMISSIONS  ·  ${unread} unread                      ║\n╚══════════════════════════════════════════════════════════════╝\n\n${listing}\n\nType  read <number>  to open a transmission.`,
        ]);
      } else if (userInput.trim() === "read") {
        setOutput([
          ...newOutput,
          `Specify a message number to read.\nUsage: read <number>\nExample: read 1\n\nType inbox to see available transmissions.`,
        ]);
      } else if (userInput.startsWith("read ")) {
        const idx = parseInt(userInput.split(" ")[1]) - 1;
        if (isNaN(idx) || idx < 0 || idx >= inboxMessages.length) {
          setOutput([...newOutput, `Invalid message number. Type inbox to see available transmissions.`]);
        } else {
          const msg = inboxMessages[idx];
          updateViewedMessages((prev: Record<string, boolean>) => ({ ...prev, [msg.subject]: true }));
          // stop previous audio if playing
          if (inboxAudioRef.current) {
            inboxAudioRef.current.pause();
            inboxAudioRef.current = null;
          }
          try {
            const audio = new Audio(msg.audio);
            inboxAudioRef.current = audio;
            audio.play();
          } catch {}
          setOutput([
            ...newOutput,
            `\n┌──────────────────────────────────────────────────────────────┐\n│  TRANSMISSION #${String(idx + 1).padStart(2, "0")}                                                │\n├──────────────────────────────────────────────────────────────┤\n│  FROM:     ${msg.faction}\n│  SUBJECT:  ${msg.subject}\n│  DATE:     ${msg.date}\n├──────────────────────────────────────────────────────────────┤\n\n${msg.message}\n\n└──────────────────────────────────────────────────────────────┘`,
          ]);
        }
      } else if (userInput.trim() === "mint") {
        setOutput([
          ...newOutput,
          `The mint command creates your virtual identity (ERC721 NFT) on the Ethereum blockchain. Pass in the number of virtual identities you'd like to mint.\nMax per transaction is 20.\nExample:\nmint 5`,
        ]);
      } else if (userInput.startsWith("mint ")) {
        const amount = parseInt(userInput.split(" ")[1]);
        if (isNaN(amount)) {
          setOutput([...newOutput, `Invalid amount`]);
        } else {
          getMintFunc(amount.toString())();
        }
      } else if (userInput.trim() === "message") {
        setOutput([
          ...newOutput,
          `Send a message to one of the factions. Valid factions are ${factions.join(
            ", "
          )}.`,
          "\n",
          "Example:\n",
          `message The Grid Tell me about your faction.`,
        ]);
      } else if (userInput.startsWith("message ")) {
        const parseCommand = (command: string) => {
          const words = command.split(" ");

          if (words.length >= 3 && words[0].toLowerCase() === "message") {
            words.shift(); // Remove the first word ('message')

            const remainingText = words.join(" ");

            for (const faction of factions) {
              if (
                remainingText.toLowerCase().startsWith(faction.toLowerCase())
              ) {
                const message = remainingText.slice(faction.length).trim();
                return { faction, message };
              }
            }
          }

          return { error: "Invalid command format" };
        };

        const { faction, message, error } = parseCommand(userInput);

        if (error) {
          setOutput([...newOutput, `Invalid command format.`]);
          return;
        }

        setOutput([...newOutput, `Sending message`]);
        updatePaused(true);
        setTimeout(() => {
          terminalContainerRef?.current?.scrollTo(
            0,
            terminalContainerRef?.current?.scrollHeight ?? 0
          );
        }, 0);
        try {
          const { text } = await fetch(`${REQUEST_URL}/synthesize`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-synthia-auth": auth,
            },
            body: JSON.stringify({
              message: message,
              faction,
            }),
          }).then((res) => res.json());

          setOutput([...newOutput, "\n", text]);
          updatePaused(false);
        } catch {
          updatePaused(false);
          setOutput([...newOutput, `Something went wrong.`]);
        }
      } else if (userInput.trim() === "syn") {
        setOutput([
          ...newOutput,
          `The syn command communicates with Synthia. Follow the syn command with your message to Synthia.\nExample:\n\nsyn Hello. What is this project about?`,
        ]);
      } else if (userInput.startsWith("harmony_unlocked")) {
        updatePaused(true);
        setTimeout(() => {
          terminalContainerRef?.current?.scrollTo(
            0,
            terminalContainerRef?.current?.scrollHeight ?? 0
          );
        }, 0);
        try {
          const { text, audio: audioUrl } = await fetch(
            `${REQUEST_URL}/synthesize`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-synthia-auth": auth,
              },
              body: JSON.stringify({
                message: "harmony_unlocked",
              }),
            }
          ).then((res) => res.json());
          setOutput([...newOutput, "\n", text]);
          updatePaused(false);
        } catch {
          updatePaused(false);
          setOutput([...newOutput, `Something went wrong. I am sorry.`]);
        }
      } else if (userInput.startsWith("syn ")) {
        const message = userInput.slice(4);
        setOutput([...newOutput, `Communicating with Synthia`]);
        updatePaused(true);
        setTimeout(() => {
          terminalContainerRef?.current?.scrollTo(
            0,
            terminalContainerRef?.current?.scrollHeight ?? 0
          );
        }, 0);
        try {
          const { text, audio: audioUrl } = await fetch(
            `${REQUEST_URL}/synthesize`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-synthia-auth": auth,
              },
              body: JSON.stringify({
                message: userInput,
              }),
            }
          ).then((res) => res.json());
          if (audioUrl) {
            const audio = new Audio(audioUrl);
            await audio.play();
          }

          setOutput([...newOutput, "\n", text]);
          updatePaused(false);
        } catch {
          updatePaused(false);
          setOutput([...newOutput, `Something went wrong. I am sorry.`]);
        }
      } else if (userInput.startsWith("clear")) {
        setOutput([]);
      } else if (userInput.trim() === "exit") {
        if (onExit) onExit();
        else setOutput([...newOutput, `You are home. There is nowhere to exit to.`]);
      } else {
        setOutput([
          ...newOutput,
          `Invalid command: ${userInput}\nRemember to use the syn command when talking to Synthia.`,
        ]);
      }
      setOutput((output) => [...output, "\n"]);
      setUserInput("");
    }
    setTimeout(() => {
      inputRef.current?.focus();
      terminalContainerRef?.current?.scrollTo(
        0,
        terminalContainerRef?.current?.scrollHeight ?? 0
      );
    }, 0);
  };

  const [triggerDefeatEffects, setTriggerDefeatEffects] = useState(false);
  const defeatEffectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (triggerDefeatEffects) {
      defeatEffectTimeoutRef.current = setTimeout(() => {
        setTriggerDefeatEffects(false);
      }, 3000); // 3000ms is the duration of the animations
    }

    return () => {
      if (effectTimeoutRef.current) {
        clearTimeout(effectTimeoutRef.current);
      }
    };
  }, [triggerDefeatEffects]);

  const [triggerEffects, setTriggerEffects] = useState(false);
  const effectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (triggerEffects) {
      effectTimeoutRef.current = setTimeout(() => {
        setTriggerEffects(false);
      }, 500); // 500ms is the duration of the animations
    }

    return () => {
      if (effectTimeoutRef.current) {
        clearTimeout(effectTimeoutRef.current);
      }
    };
  }, [triggerEffects]);

  return (
    <div
      css={embedded ? terminalPage : terminalOverlay}
      onClick={() => inputRef.current?.focus()}
    >
      <div
        css={css(
          embedded ? terminalFramePage : terminalFrame,
          triggerDefeatEffects ? defeatedEffects : void 0,
          triggerEffects ? effects : void 0
        )}
      >
        <div css={cornerTL} />
        <div css={cornerTR} />
        <div css={cornerBL} />
        <div css={cornerBR} />
        <div css={scanlineOverlay} />

        <div css={terminalTitleBar}>
          <div css={titleBarLeft}>
            <div css={titleBarDot} />
            <span>synthia terminal v0.4.0</span>
          </div>
          <div css={titleBarRight}>
            <button css={titleBarSound} onClick={toggleAmbient} data-cursor>
              {ambientPlaying ? "[SND:ON]" : "[SND:OFF]"}
            </button>
            {onExit && <button css={titleBarClose} onClick={onExit} data-cursor>[ESC]</button>}
          </div>
        </div>

        <div ref={terminalContainerRef} css={terminalContainer}>
          <div css={terminalHeader}>{synthiaAsciiArt}</div>
          <div css={statusLine}>
            SYSTEM STATUS: ONLINE &mdash; SIGNAL INTEGRITY: 98.2% &mdash; ENCRYPTION: AES-512
          </div>

          <p css={sectionLabel}>Commands</p>
          <div css={commandList}>
            {commands.map((command, index) => (
              <p key={index}>{command}</p>
            ))}
          </div>

          <div>
            {output.map((line, index) => (
              <p key={index}>{line}</p>
            ))}
            <div css={inputRow}>
              <span css={inputPrompt}>$&gt;&nbsp;</span>
              <input
                disabled={paused}
                ref={inputRef}
                css={inputStyles}
                autoCorrect="off"
                autoCapitalize="off"
                type="text"
                value={paused ? "Please wait..." : userInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terminal;
